import { useState, useEffect } from 'react';
import { X, UserPlus, Trash2, Users, Shield } from 'lucide-react';
import api from '../lib/api';
import Button from './ui/Button';
import Badge from './ui/Badge';

interface JobAssignment {
  id: string;
  userId: string;
  jobId: string;
  role: 'PRIMARY' | 'COLLABORATOR' | 'REVIEWER';
  assignedAt: string;
  assignedById: string;
  user: {
    id: string;
    fullName?: string;
    email: string;
  };
  assignedBy: {
    id: string;
    fullName?: string;
  };
}

interface User {
  id: string;
  fullName?: string;
  email: string;
  role: string;
  roleInOrg?: string;
}

interface JobAssignmentModalProps {
  jobId: string;
  jobTitle: string;
  onClose: () => void;
  onAssignmentChange?: () => void;
}

const ASSIGNMENT_ROLES = [
  { value: 'PRIMARY', label: 'Primary Owner', description: 'Full responsibility for the job' },
  { value: 'COLLABORATOR', label: 'Collaborator', description: 'Can work on the job' },
  { value: 'REVIEWER', label: 'Reviewer', description: 'Can review and approve' },
];

export default function JobAssignmentModal({ jobId, jobTitle, onClose, onAssignmentChange }: JobAssignmentModalProps) {
  const [assignments, setAssignments] = useState<JobAssignment[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState<'PRIMARY' | 'COLLABORATOR' | 'REVIEWER'>('COLLABORATOR');
  const [isAssigning, setIsAssigning] = useState(false);

  useEffect(() => {
    loadData();
  }, [jobId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Get current user to find organization ID
      const userResponse = await api.get('/auth/me');
      const organizationId = userResponse.data.organizationId;
      
      if (!organizationId) {
        console.error('User has no organization');
        setLoading(false);
        return;
      }
      
      const [assignmentsRes, membersRes] = await Promise.all([
        api.get(`/jobs/${jobId}/assignments`),
        api.get(`/organizations/${organizationId}/members`),
      ]);
      
      setAssignments(assignmentsRes.data);
      // Convert members to user format
      const teamMembers = membersRes.data.map((member: any) => ({
        id: member.id,
        email: member.email,
        fullName: member.name || member.email,
        role: 'AGENCY',
        roleInOrg: member.roleInOrg,
      }));
      setUsers(teamMembers);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedUserId) return;

    try {
      setIsAssigning(true);
      await api.post(`/jobs/${jobId}/assignments`, {
        userIds: [selectedUserId],
        role: selectedRole,
      });
      await loadData();
      setSelectedUserId('');
      setSelectedRole('COLLABORATOR');
      onAssignmentChange?.();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to assign user');
    } finally {
      setIsAssigning(false);
    }
  };

  const handleRemove = async (assignmentId: string) => {
    if (!confirm('Are you sure you want to remove this assignment?')) return;

    try {
      await api.delete(`/jobs/assignments/${assignmentId}`);
      await loadData();
      onAssignmentChange?.();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to remove assignment');
    }
  };

  const handleRoleChange = async (assignmentId: string, newRole: string) => {
    try {
      await api.put(`/jobs/assignments/${assignmentId}`, {
        role: newRole,
      });
      await loadData();
      onAssignmentChange?.();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to update role');
    }
  };

  const getRoleBadge = (role: string) => {
    const variants: Record<string, any> = {
      PRIMARY: 'default',
      COLLABORATOR: 'secondary',
      REVIEWER: 'outline',
    };
    return variants[role] || 'secondary';
  };

  const availableUsers = users.filter(
    (user) => !assignments.some((a) => a.userId === user.id)
  );

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="glass-card max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-glass">
          <div>
            <h2 className="text-2xl font-semibold tracking-tightest text-glass-text">Manage Assignments</h2>
            <p className="text-sm text-glass-text-2 mt-1">{jobTitle}</p>
          </div>
          <button
            onClick={onClose}
            className="text-glass-text-3 hover:text-glass-text transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto glass-scroll p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent2 mx-auto"></div>
              <p className="text-glass-text-3 mt-2">Loading...</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Add Assignment */}
              <div className="rounded-xl border border-accent2/30 bg-accent2/10 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <UserPlus className="w-5 h-5 text-accent2" />
                  <h3 className="font-semibold text-glass-text">Assign Team Member</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="md:col-span-1">
                    <label className="block text-sm font-medium text-glass-text-2 mb-1">
                      Team Member
                    </label>
                    <select
                      value={selectedUserId}
                      onChange={(e) => setSelectedUserId(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-glass bg-glass-2 text-glass-text focus:outline-none focus:ring-2 focus:ring-accent2/40"
                      disabled={isAssigning || availableUsers.length === 0}
                    >
                      <option value="">Select user...</option>
                      {availableUsers.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.fullName || user.email} {user.roleInOrg && `(${user.roleInOrg})`}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-1">
                    <label className="block text-sm font-medium text-glass-text-2 mb-1">
                      Assignment Role
                    </label>
                    <select
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value as any)}
                      className="w-full px-3 py-2 rounded-lg border border-glass bg-glass-2 text-glass-text focus:outline-none focus:ring-2 focus:ring-accent2/40"
                      disabled={isAssigning}
                    >
                      {ASSIGNMENT_ROLES.map((role) => (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-1 flex items-end">
                    <Button
                      onClick={handleAssign}
                      disabled={!selectedUserId || isAssigning}
                      className="w-full"
                    >
                      {isAssigning ? 'Assigning...' : 'Assign'}
                    </Button>
                  </div>
                </div>

                {availableUsers.length === 0 && (
                  <p className="text-sm text-glass-text-3 mt-2">
                    All available team members are already assigned to this job.
                  </p>
                )}
              </div>

              {/* Current Assignments */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-5 h-5 text-glass-text-2" />
                  <h3 className="font-semibold text-glass-text">
                    Current Assignments ({assignments.length})
                  </h3>
                </div>

                {assignments.length === 0 ? (
                  <div className="text-center py-8 rounded-xl border border-glass bg-glass-2">
                    <Shield className="w-12 h-12 text-glass-text-3 mx-auto mb-2" />
                    <p className="text-glass-text-2">No team members assigned yet</p>
                    <p className="text-sm text-glass-text-3 mt-1">
                      Assign team members to collaborate on this job
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {assignments.map((assignment) => (
                      <div
                        key={assignment.id}
                        className="flex items-center justify-between p-4 rounded-xl border border-glass bg-glass-2 hover:bg-glass-3 transition-colors"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-accent2/15 border border-accent2/30 rounded-full flex items-center justify-center">
                              <span className="text-accent2 font-semibold">
                                {(assignment.user.fullName || assignment.user.email).charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <div className="font-medium text-glass-text">
                                {assignment.user.fullName || assignment.user.email}
                              </div>
                              <div className="text-sm text-glass-text-3">
                                {assignment.user.email}
                              </div>
                            </div>
                          </div>
                          <div className="mt-2 text-xs text-glass-text-3">
                            Assigned by {assignment.assignedBy.fullName || assignment.assignedBy.id} on{' '}
                            {new Date(assignment.assignedAt).toLocaleDateString()}
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <select
                            value={assignment.role}
                            onChange={(e) => handleRoleChange(assignment.id, e.target.value)}
                            className="px-3 py-1.5 rounded-lg border border-glass bg-glass-2 text-glass-text text-sm focus:outline-none focus:ring-2 focus:ring-accent2/40"
                          >
                            {ASSIGNMENT_ROLES.map((role) => (
                              <option key={role.value} value={role.value}>
                                {role.label}
                              </option>
                            ))}
                          </select>

                          <button
                            onClick={() => handleRemove(assignment.id)}
                            className="p-2 text-rose-500 dark:text-rose-300 hover:bg-rose-500/10 rounded-lg transition-colors"
                            title="Remove assignment"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Role Descriptions */}
              <div className="rounded-xl border border-glass bg-glass-2 p-4">
                <h4 className="font-medium text-glass-text mb-2 text-sm">Assignment Role Descriptions</h4>
                <div className="space-y-2">
                  {ASSIGNMENT_ROLES.map((role) => (
                    <div key={role.value} className="flex items-start gap-2">
                      <Badge variant={getRoleBadge(role.value)} className="mt-0.5">
                        {role.label}
                      </Badge>
                      <span className="text-sm text-glass-text-2">{role.description}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-glass bg-glass-2">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}
